import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Warning, CheckCircle } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROD_CHECKLIST = [
  { key: 'payfast', label: 'PayFast merchant credentials', ok: (f) => f.payfast_configured },
  { key: 'resend', label: 'Resend email (RESEND_API_KEY)', ok: (f) => f.resend_configured },
  { key: 'jwt', label: 'Custom JWT secret set', ok: (f) => f.jwt_custom },
  { key: 'wa', label: 'WhatsApp number set', ok: (f) => !!f.whatsapp_number },
  { key: 'email', label: 'Contact email set', ok: (f) => !!f.contact_email },
  { key: 'social', label: 'At least one social link', ok: (f) => !!(f.social_links?.instagram || f.social_links?.facebook) }
];

const AdminSettings = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState({});
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchSettings();
  }, [authLoading, user, navigate]);

  const fetchSettings = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } });
    setForm(res.data || {});
  };

  const save = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    await axios.put(`${API}/admin/settings`, {
      ...form,
      shipping_fee: Number(form.shipping_fee || 0),
      free_shipping_threshold: Number(form.free_shipping_threshold || 0),
      vat_rate: Number(form.vat_rate || 0),
      vat_inclusive_prices: Boolean(form.vat_inclusive_prices),
      sedgefield_local_delivery_enabled: Boolean(form.sedgefield_local_delivery_enabled),
      sedgefield_local_delivery_label: form.sedgefield_local_delivery_label || 'Sedgefield',
      social_links: {
        instagram: form.social_links?.instagram || '',
        facebook: form.social_links?.facebook || '',
        tiktok: form.social_links?.tiktok || '',
        x: form.social_links?.x || ''
      }
    }, { headers: { Authorization: `Bearer ${token}` } });
    setMessage('Settings saved');
    setTimeout(() => setMessage(''), 2500);
  };

  return (
    <div className="min-h-screen bg-[#F3E4CC]">
      <div className="bg-[#15110E] text-[#F3E4CC] py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/admin" className="inline-flex items-center gap-2 text-[#D7B98C] hover:text-white mb-4"><ArrowLeft size={20} /> Back to Dashboard</Link>
          <h1 className="font-heading text-3xl">Store Settings</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Production readiness checklist */}
        {Object.keys(form).length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <h2 className="font-heading text-lg text-[#15110E] mb-3">Production Readiness</h2>
            <div className="space-y-2">
              {PROD_CHECKLIST.map(item => {
                const pass = item.ok(form);
                return (
                  <div key={item.key} className="flex items-center gap-2 text-sm">
                    {pass
                      ? <CheckCircle size={16} weight="fill" className="text-green-500 flex-shrink-0" />
                      : <Warning size={16} weight="fill" className="text-yellow-500 flex-shrink-0" />}
                    <span className={pass ? 'text-[#2C1A12]' : 'text-yellow-700 font-medium'}>{item.label}</span>
                    {!pass && <span className="ml-auto text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Set in .env.production</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <form onSubmit={save} className="bg-white rounded-lg shadow-sm p-5 space-y-3">
          <input className="w-full border border-[#D7B98C] px-3 py-2 rounded" placeholder="Store name" value={form.store_name || ''} onChange={(e) => setForm({ ...form, store_name: e.target.value })} />
          <input className="w-full border border-[#D7B98C] px-3 py-2 rounded" placeholder="Contact email" value={form.contact_email || ''} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
          <input className="w-full border border-[#D7B98C] px-3 py-2 rounded" placeholder="WhatsApp number" value={form.whatsapp_number || ''} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} />
          <input className="w-full border border-[#D7B98C] px-3 py-2 rounded" placeholder="Instagram URL" value={form.social_links?.instagram || ''} onChange={(e) => setForm({ ...form, social_links: { ...(form.social_links || {}), instagram: e.target.value } })} />
          <input className="w-full border border-[#D7B98C] px-3 py-2 rounded" placeholder="Facebook URL" value={form.social_links?.facebook || ''} onChange={(e) => setForm({ ...form, social_links: { ...(form.social_links || {}), facebook: e.target.value } })} />
          <input className="w-full border border-[#D7B98C] px-3 py-2 rounded" placeholder="TikTok URL" value={form.social_links?.tiktok || ''} onChange={(e) => setForm({ ...form, social_links: { ...(form.social_links || {}), tiktok: e.target.value } })} />
          <input className="w-full border border-[#D7B98C] px-3 py-2 rounded" placeholder="X / Twitter URL" value={form.social_links?.x || ''} onChange={(e) => setForm({ ...form, social_links: { ...(form.social_links || {}), x: e.target.value } })} />
          <input type="number" className="w-full border border-[#D7B98C] px-3 py-2 rounded" placeholder="Shipping fee" value={form.shipping_fee || ''} onChange={(e) => setForm({ ...form, shipping_fee: e.target.value })} />
          <input type="number" className="w-full border border-[#D7B98C] px-3 py-2 rounded" placeholder="Complimentary delivery threshold" value={form.free_shipping_threshold || ''} onChange={(e) => setForm({ ...form, free_shipping_threshold: e.target.value })} />
          <input type="number" step="0.01" className="w-full border border-[#D7B98C] px-3 py-2 rounded" placeholder="VAT rate" value={form.vat_rate || ''} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} />
          <input className="w-full border border-[#D7B98C] px-3 py-2 rounded" placeholder="Local delivery area label" value={form.sedgefield_local_delivery_label || ''} onChange={(e) => setForm({ ...form, sedgefield_local_delivery_label: e.target.value })} />
          <label className="flex items-center gap-3 text-sm text-[#2C1A12]">
            <input type="checkbox" checked={Boolean(form.vat_inclusive_prices)} onChange={(e) => setForm({ ...form, vat_inclusive_prices: e.target.checked })} />
            Prices shown across the store are VAT-inclusive
          </label>
          <label className="flex items-center gap-3 text-sm text-[#2C1A12]">
            <input type="checkbox" checked={Boolean(form.sedgefield_local_delivery_enabled)} onChange={(e) => setForm({ ...form, sedgefield_local_delivery_enabled: e.target.checked })} />
            Enable complimentary local delivery for the configured area
          </label>
          <div className="text-sm text-[#3A2418] bg-[#F3E4CC] border border-[#D7B98C] rounded px-3 py-2">
            Resend status: {form.resend_configured ? 'Configured' : 'Not configured'}
          </div>
          <button className="bg-[#B56A35] text-white px-4 py-2 rounded">Save Settings</button>
          {message && <p className="text-green-700 text-sm">{message}</p>}
        </form>
      </div>
    </div>
  );
};

export default AdminSettings;
